import { upload } from '../../lib/api.js'
import { fileIcon, formatBytes } from '../../lib/format.js'
import { fromWall } from '../../lib/time.js'
import { TEMPLATE_PLACEHOLDERS, fillTemplate, firstRecipient, mailboxName } from '../../lib/mail.js'
import { normalize } from '../../lib/markdown.js'
import { routeHref } from '../../lib/router.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-compose ---
let __fez_component_0;
{
// Same cap as `MAX_ATTACHMENT_BYTES` on the server.
const MAX_BYTES = 8 * 1024 * 1024

// Autosave runs this long after the last change.
const AUTOSAVE_MS = 2000
const SAVE_LABELS = { saving: 'Saving...', saved: 'Saved', error: 'Not saved' }

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
    reader.onerror = () => reject(new Error('Could not read the file'))
    reader.readAsDataURL(file)
  })
}

function hasFiles(event) {
  return Array.from(event.dataTransfer?.types || []).includes('Files')
}

function addressList(value) {
  return (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

// The toast's Undo outlives the compose form: take the send back and reopen it as a draft.
async function undoSend(container, email, mailbox) {
  try {
    const draft = await window.App.api(`container/${container}/send_cancel`, { email, mailbox }, { silent: true })
    if (window.ComposeDock) window.ComposeDock.open({ container, mailbox: draft.mailbox, draft: draft.ref })
    else window.App.go(routeHref('compose', { container, mailbox: draft.mailbox, draft: draft.ref }))
    window.Toast.info('Sending undone; the message is back in compose.')
  } catch (error) {
    window.Toast.error(error.code === 'conflict' ? 'Too late: it is already on its way.' : error.message)
  }
}
__fez_component_0 = class {
  PROPS = {
    container: { type: String, required: true },
    mailbox: String,
    mailboxes: Array,
    domains: Array,
    domain: String,
    from_local: String,
    to: String,
    cc: String,
    bcc: String,
    subject: String,
    body: String,
    in_reply_to: String,
    draft: String,
    attachments: Array,
    autofocus: Boolean,
    on_done: { type: Function, state: true, default: () => {} },
  }

  init() {
    this.state.fromNames = this.fromNames()
    this.state.fromDomains = this.props.domains.map((domain) => ({ value: domain.name, label: domain.name }))
    const [mailbox = '', local = '', host = ''] = this.pickIdentity().split('|')
    this.state.fromName = mailbox ? `${mailbox}|${local}` : ''
    this.state.fromDomain = host
    this.state.fromMailbox = mailbox
    this.state.to = this.props.to || ''
    this.state.cc = this.props.cc || ''
    this.state.bcc = this.props.bcc || ''
    this.state.subject = this.props.subject || ''
    // A draft already carries its signature. `signature` is kept as the editor writes it back, so
    // it can be found in the message.
    const signature = this.props.draft ? '' : this.signatureBlock(this.state.fromMailbox)
    this.state.signature = signature && normalize(signature)
    this.state.body = `${signature}${this.props.body || ''}`
    this.state.attachments = this.props.attachments.map((attachment) => ({ ...attachment, id: attachment.file, status: 'done' }))
    this.state.uploads = {}
    this.state.nextId = 0
    this.state.draft = this.props.draft || ''
    this.state.showCc = Boolean(this.state.cc)
    this.state.focusCc = false
    this.state.showBcc = Boolean(this.state.bcc)
    this.state.focusBcc = false
    this.state.showSchedule = false
    this.state.dragging = false
    this.state.dragDepth = 0
    this.state.sending = false
    this.state.saving = false
    // Autosave: `baseline` is the message as opened (a reply's quote and signature, a draft as
    // saved) and `savedFields` what the draft holds, both as `fields()` text; `pending` waits for
    // the save in flight (`saveRun`); `closed` stops it all while sending or discarding.
    this.state.saveStatus = ''
    this.state.saveError = ''
    this.state.saveTimer = 0
    this.state.saveRun = null
    this.state.pending = null
    this.state.baseline = ''
    this.state.savedFields = ''
    this.state.closed = false
    this.state.errors = {}
    this.state.templatesOpen = false
    this.state.templates = []
    this.state.templatesFor = ''
    this.state.templateHint = `Placeholders filled on insert: ${TEMPLATE_PLACEHOLDERS.join(', ')}`
  }

  onMount() {
    // A file dropped beside the form must not open in the tab and lose the message.
    this.on(window, 'dragover', (event) => {
      if (!hasFiles(event)) return
      event.preventDefault()
      if (!this.root.contains(event.target)) event.dataTransfer.dropEffect = 'none'
    })
    this.on(window, 'drop', (event) => hasFiles(event) && event.preventDefault())
    if (this.props.autofocus) {
      this.setTimeout(() => {
        const to = this.root.querySelector('input[name="to"]')
        if (to && !to.value) to.focus({ preventScroll: true })
        else this.bodyEditor()?.focusStart()
        this.root.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 30)
    }
    this.state.baseline = this.fields()
    if (this.state.draft) this.state.savedFields = this.state.baseline
    // Leaving the tab or the page saves at once; the request outlives the page (keepalive).
    this.on(document, 'visibilitychange', () => document.visibilityState === 'hidden' && this.flush())
    this.on(window, 'pagehide', () => this.flush())
  }

  // Leaving the form (another conversation, another page) keeps what was written.
  onDestroy() {
    for (const controller of Object.values(this.state.uploads)) controller.abort()
    this.flush()
  }

  beforeRender() {
    const attachments = this.state.attachments
    this.state.uploading = attachments.some((attachment) => attachment.status === 'uploading')
    this.state.cannotSend = !this.state.fromNames.length && this.props.mailboxes.length > 0
    this.state.manyDomains = this.state.fromDomains.length > 1
    this.state.sendBlocked = !this.props.domains.length || this.state.uploading || this.state.cannotSend
    this.state.saveLabel = SAVE_LABELS[this.state.saveStatus] || ''
    this.state.cards = attachments.map((attachment) => {
      const percent = Math.round((attachment.progress || 0) * 100)
      let meta = attachment.size == null ? '' : formatBytes(attachment.size)
      if (attachment.status === 'uploading') meta = `Uploading ${percent}%`
      if (attachment.status === 'error') meta = attachment.error
      return {
        id: attachment.id,
        filename: attachment.filename || 'attachment',
        status: attachment.status,
        icon: attachment.status === 'error' ? 'warning-circle' : fileIcon(attachment.content_type, attachment.filename),
        meta,
        percent,
      }
    })
  }

  // `mbx|local` for each mailbox name and alias; every one goes with every active domain.
  fromNames() {
    // Members who only read a mailbox do not send from it.
    return this.props.mailboxes.filter((mailbox) => mailbox.can_send !== false).flatMap((mailbox) =>
      (mailbox.aliases || []).map((local) => ({ value: `${mailbox.ref}|${local}`, label: mailbox.display_name ? `${local} - ${mailbox.display_name}` : local })),
    )
  }

  // The starting `mbx|local|host`.
  pickIdentity() {
    const hosts = this.props.domains.length ? this.props.domains.map((domain) => domain.name) : ['']
    const values = this.state.fromNames.flatMap((name) => hosts.map((host) => `${name.value}|${host}`))
    const mailbox = this.props.mailbox || this.props.mailboxes[0]?.ref || ''
    const box = this.props.mailboxes.find((item) => item.ref === mailbox)
    const first = this.props.domains[0]?.name || ''
    const wanted = [
      this.props.from_local || this.props.domain ? `${mailbox}|${this.props.from_local || mailboxName(box)}|${this.props.domain || first}` : '',
      Fez.localStorage.get(`mail-from:${mailbox}`, ''),
      `${mailbox}|${mailboxName(box)}|${first}`,
    ]
    return wanted.find((value) => values.includes(value)) || values[0] || ''
  }

  bodyEditor() {
    const node = this.root.querySelector('.ui-md-editor-fez')
    return node && Fez(node)
  }

  signatureBlock(mailbox) {
    const found = this.props.mailboxes.find((item) => item.ref === mailbox)
    return found && found.signature ? `\n\n-- \n${found.signature}` : ''
  }

  // The name or the domain select changed; a new mailbox swaps the signature and templates.
  fromChanged(event) {
    if (event.target.name === 'from_domain') this.state.fromDomain = event.target.value
    if (event.target.name !== 'from_name') return
    this.state.fromName = event.target.value
    const mailbox = event.target.value.split('|')[0]
    if (mailbox === this.state.fromMailbox) return
    this.state.fromMailbox = mailbox
    this.state.templatesOpen = false
    const editor = this.bodyEditor()
    const body = editor.value()
    const block = this.signatureBlock(mailbox)
    const next = block && normalize(block)
    const current = this.state.signature
    // Leading blank lines keep the empty line above the signature to write in.
    if (current && body.includes(current)) editor.setValue(`${body.startsWith(current) ? '\n\n' : ''}${body.replace(current, next)}`)
    else if (next) editor.setValue(`${block}${body}`)
    this.state.signature = next
  }

  // Templates of the From mailbox, loaded when the menu first opens for it.
  async toggleTemplates() {
    this.state.templatesOpen = !this.state.templatesOpen
    if (this.state.templatesOpen && this.state.templatesFor !== this.state.fromMailbox) await this.loadTemplates()
  }

  async loadTemplates() {
    const mailbox = this.state.fromMailbox
    const list = await window.App.api(`container/${this.props.container}/templates`, { mailbox }, { silent: true })
    this.state.templates = list.items
    this.state.templatesFor = mailbox
  }

  // At the cursor, else above the signature; the subject only when there is none yet. Placeholders
  // take the first To recipient (its name in the field, else my contact's) and the From mailbox.
  async useTemplate(ref) {
    const template = this.state.templates.find((item) => item.ref === ref)
    if (!template) return
    const editor = this.bodyEditor()
    const atCursor = editor.focused()
    const subject = this.root.querySelector('input[name="subject"]')
    const to = this.root.querySelector('input[name="to"]')?.value || ''
    const mailbox = this.props.mailboxes.find((item) => item.ref === this.state.fromMailbox)?.display_name
    const fill = { to, mailbox, contact: await this.contactName(to, `${template.subject} ${template.body}`) }
    const text = fillTemplate(template.body, fill)
    if (atCursor) editor.insert(text)
    else {
      const body = editor.value()
      const at = this.state.signature ? body.indexOf(this.state.signature) : -1
      editor.setValue(at >= 0 ? `${body.slice(0, at)}${text}\n\n${body.slice(at)}` : `${body}\n\n${text}`)
    }
    if (subject && !subject.value.trim() && template.subject) subject.value = fillTemplate(template.subject, fill)
    this.state.templatesOpen = false
    this.changed()
  }

  // My contact's name for the first recipient, looked up only when the field names nobody and the
  // template asks for a name; a mailbox session has no address book.
  async contactName(to, text) {
    const { name, email } = firstRecipient(to)
    if (name || !email || !this.globalState.me || !/\{(first_name|name)\}/.test(text)) return ''
    try {
      const found = await window.App.api('contact/list', { address: email.toLowerCase() }, { silent: true })
      return found.items[0]?.name || ''
    } catch {
      return ''
    }
  }

  async saveTemplate() {
    const form = this.formData(this.root.querySelector('form'))
    const body = (form.body || '').replace(this.state.signature, '').trim()
    if (!body) return window.Toast?.error('Write the message first')
    const name = await window.Dialog.open('name-dialog', { label: 'Template name', value: (form.subject || '').slice(0, 80), confirm: 'Save template' }, { title: 'Save as a template' })
    if (!name) return
    await window.App.api(`container/${this.props.container}/template_save`, { mailbox: this.state.fromMailbox, name, subject: form.subject || '', body })
    await this.loadTemplates()
  }

  async deleteTemplate(ref, event) {
    event.stopPropagation()
    await window.App.api(`container/${this.props.container}/template_delete`, { mailbox: this.state.fromMailbox, template: ref })
    await this.loadTemplates()
  }

  // Anything written beyond the starting signature, or a file.
  hasContent(form = this.formData(this.root.querySelector('form'))) {
    const typed = ['to', 'cc', 'bcc', 'subject'].some((key) => (form[key] || '').trim())
    const body = (form.body || '').trim()
    return typed || (body && body !== this.state.signature) || this.state.attachments.length > 0
  }

  openCc() {
    this.state.focusCc = true
    this.state.showCc = true
  }

  openBcc() {
    this.state.focusBcc = true
    this.state.showBcc = true
  }

  openSchedule() {
    this.state.showSchedule = true
  }

  closeSchedule() {
    this.state.showSchedule = false
    this.clearError('send_at')
  }

  clearError(key) {
    if (!(key in this.state.errors)) return
    const { [key]: _, ...rest } = this.state.errors
    this.state.errors = rest
  }

  pickFiles() {
    this.state.fileInput.click()
  }

  chosen(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    this.addFiles(files)
  }

  dragEnter(event) {
    if (!hasFiles(event)) return
    event.preventDefault()
    this.state.dragDepth++
    this.state.dragging = true
  }

  dragOver(event) {
    if (!hasFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }

  // enter/leave fire for every child crossed, so only the last leave ends the drag.
  dragLeave(event) {
    if (!hasFiles(event)) return
    this.state.dragDepth = Math.max(0, this.state.dragDepth - 1)
    if (!this.state.dragDepth) this.state.dragging = false
  }

  drop(event) {
    if (!hasFiles(event)) return
    event.preventDefault()
    this.state.dragDepth = 0
    this.state.dragging = false
    this.addFiles(Array.from(event.dataTransfer.files || []))
  }

  // Text paste stays text; Office apps put a picture of the text next to it on the clipboard.
  paste(event) {
    const clipboard = event.clipboardData
    const files = Array.from(clipboard?.files || [])
    if (!files.length || Array.from(clipboard.types || []).includes('text/plain')) return
    event.preventDefault()
    this.addFiles(files)
  }

  addFiles(files) {
    this.clearError('attachments')
    for (const file of files) {
      const item = {
        id: `upload-${++this.state.nextId}`,
        filename: file.name || 'attachment',
        content_type: file.type || 'application/octet-stream',
        size: file.size,
        status: 'uploading',
        progress: 0,
      }
      if (!file.size) Object.assign(item, { status: 'error', error: 'The file is empty' })
      else if (file.size > MAX_BYTES) Object.assign(item, { status: 'error', error: `${formatBytes(file.size)} - the limit is ${formatBytes(MAX_BYTES)}` })
      this.state.attachments = this.state.attachments.concat([item])
      if (item.status === 'uploading') this.upload(item.id, file)
    }
  }

  async upload(id, file) {
    const controller = new AbortController()
    this.state.uploads[id] = controller
    try {
      const data = await readBase64(file)
      if (controller.signal.aborted) return
      const uploaded = await upload(
        `container/${this.props.container}/file_upload`,
        { filename: file.name || 'attachment', content_type: file.type || 'application/octet-stream', data },
        { signal: controller.signal, onProgress: (progress) => this.patch(id, { progress }) },
      )
      this.patch(id, { status: 'done', file: uploaded.file, size: uploaded.size })
      this.changed()
    } catch (error) {
      if (error.code !== 'aborted') this.patch(id, { status: 'error', error: error.message })
    } finally {
      delete this.state.uploads[id]
    }
  }

  patch(id, change) {
    this.state.attachments = this.state.attachments.map((attachment) => (attachment.id === id ? { ...attachment, ...change } : attachment))
  }

  removeAttachment(id) {
    this.state.uploads[id]?.abort()
    this.state.attachments = this.state.attachments.filter((attachment) => attachment.id !== id)
    if (!this.attachmentError()) this.clearError('attachments')
    this.changed()
  }

  // The fields send and draft_save share.
  message(form) {
    const [mailbox = '', local = '', domain = ''] = (form.from || '').split('|')
    return {
      mailbox,
      from_local: local || undefined,
      domain: domain || undefined,
      to: (form.to || '').trim(),
      cc: addressList(form.cc),
      bcc: addressList(form.bcc),
      subject: form.subject || '',
      body: form.body || '',
      in_reply_to: this.props.in_reply_to || undefined,
      attachments: this.state.attachments
        .filter((attachment) => attachment.status === 'done')
        .map((attachment) => ({
          file: attachment.file,
          filename: attachment.filename,
          content_type: attachment.content_type,
        })),
    }
  }

  attachmentError() {
    return this.state.attachments.some((attachment) => attachment.status === 'error') ? 'Remove the files that could not be attached' : null
  }

  // The message as autosave compares and saves it.
  fields(form = this.formData(this.root.querySelector('form'))) {
    return JSON.stringify(this.message(form))
  }

  // Any edit saves the draft AUTOSAVE_MS after the last one.
  changed() {
    if (this.state.closed) return
    clearTimeout(this.state.saveTimer)
    this.state.saveTimer = setTimeout(() => this.save(), AUTOSAVE_MS)
  }

  // Saves at once what the timer would have.
  flush() {
    if (this.state.closed) return
    clearTimeout(this.state.saveTimer)
    this.save()
  }

  // One save at a time: the fields are read now, and what is asked for while a save runs waits
  // for it, only the latest. `force` (Save draft) saves even a message as it was opened.
  save(force = false) {
    if (this.state.closed) return Promise.resolve()
    const form = this.formData(this.root.querySelector('form'))
    if (!form.from) return Promise.resolve()
    this.state.pending = { fields: this.fields(form), content: this.hasContent(form), force: force || Boolean(this.state.pending?.force) }
    this.state.saveRun ||= this.saveLoop()
    return this.state.saveRun
  }

  async saveLoop() {
    try {
      while (this.state.pending && !this.state.closed) {
        const { fields, content, force } = this.state.pending
        this.state.pending = null
        await this.saveOnce(fields, content, force)
      }
    } finally {
      this.state.saveRun = null
    }
  }

  // The first save makes the draft and later ones replace it. No draft is made for a message that
  // is empty or as it was opened (a reply's quote and signature), unless forced.
  async saveOnce(fields, content, force) {
    if (fields === this.state.savedFields) {
      if (this.state.saveStatus === 'error') this.state.saveStatus = 'saved'
      return
    }
    if (!this.state.draft && !force && (fields === this.state.baseline || !content)) return
    this.state.saveStatus = 'saving'
    try {
      const draft = await window.App.api(`container/${this.props.container}/draft_save`, { ...JSON.parse(fields), draft: this.state.draft || undefined }, { silent: true, keepalive: true })
      this.state.draft = draft.ref
      this.state.savedFields = fields
      this.state.saveStatus = 'saved'
      this.state.saveError = ''
    } catch (error) {
      // Sent or deleted elsewhere, so the server refuses it: Send names no draft now, and the next
      // change starts a new one.
      if (error.code === 'not_found' && this.state.draft) {
        this.state.draft = ''
        this.state.savedFields = ''
      }
      this.state.saveStatus = 'error'
      this.state.saveError = error.message
    }
  }

  // Sending or discarding: autosave stops, and the save in flight lands first, so its draft is the
  // one `send` or `draft_delete` takes and nothing saves after it. `reopen` resumes on failure.
  async hold() {
    this.state.closed = true
    clearTimeout(this.state.saveTimer)
    await this.state.saveRun
  }

  reopen() {
    this.state.closed = false
    this.changed()
  }

  async saveDraft() {
    if (this.state.closed) return
    const form = this.formData(this.root.querySelector('form'))
    const errors = {}
    if (!form.from) errors.from = 'Pick a mailbox'
    const attachmentError = this.attachmentError()
    if (attachmentError) errors.attachments = attachmentError
    this.state.errors = errors
    if (Object.keys(errors).length) return
    clearTimeout(this.state.saveTimer)
    this.state.saving = true
    try {
      await this.save(true)
    } finally {
      this.state.saving = false
    }
    if (this.state.saveStatus === 'error') return window.Toast.error(this.state.saveError)
    window.Toast.success('Draft saved')
    this.state.on_done(true)
  }

  async discard() {
    if (this.state.closed) return
    await this.hold()
    if (this.state.draft) {
      try {
        await window.App.api(`container/${this.props.container}/draft_delete`, { draft: this.state.draft }, { silent: true })
      } catch (error) {
        if (error.code !== 'not_found') {
          window.Toast.error(error.message)
          return this.reopen()
        }
      }
    }
    this.state.on_done(false)
  }

  async send(event) {
    event.preventDefault()
    if (this.state.sendBlocked || this.state.closed) return
    const form = this.formData(event.target)
    const message = this.message(form)
    const errors = {}
    if (!message.mailbox) errors.from = 'Pick a mailbox'
    else if (!message.domain) errors.from = 'Add an active domain first'
    if (!(form.to || '').trim()) errors.to = 'Add a recipient'
    const sendAt = this.state.showSchedule && form.send_at ? fromWall(form.send_at) : null
    if (this.state.showSchedule && !sendAt) errors.send_at = 'Pick a date and time'
    else if (sendAt && !(sendAt > new Date())) errors.send_at = 'Pick a time in the future'
    const attachmentError = this.attachmentError()
    if (attachmentError) errors.attachments = attachmentError
    this.state.errors = errors
    if (Object.keys(errors).length) return
    this.state.sending = true
    await this.hold()
    try {
      const result = await window.App.api(
        `container/${this.props.container}/send`,
        { ...message, send_at: sendAt ? sendAt.toISOString() : undefined, undo_seconds: sendAt ? undefined : 10, draft: this.state.draft || undefined },
        { silent: true },
      )
      Fez.localStorage.set(`mail-from:${message.mailbox}`, form.from)
      this.state.on_done(true)
      const container = this.props.container
      if (sendAt) window.Toast.success('Message scheduled')
      else window.Toast.success('Message sent', { ms: 10000, action: { label: 'Undo', run: () => undoSend(container, result.email, message.mailbox) } })
    } catch (error) {
      if (error.code !== 'validation_failed') window.Toast.error(error.message)
      const details = { ...(error.details || {}) }
      details.from = details.from_local || details.domain || details.mailbox
      this.state.errors = details
      this.reopen()
    } finally {
      this.state.sending = false
    }
  }

  CSS = `:fez {
display:block;form{display:grid;gap:var(--space-4);width:100%}.sender{display:grid;gap:6px}.sender-label{font-size:var(--text-sm);font-weight:var(--weight-semibold)}.from-row{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2)}.from-name{flex:1 1 12rem;min-width:0}.from-host{display:flex;align-items:center;gap:var(--space-2);min-width:0;max-width:100%}.from-at,.from-domain{color:var(--color-text-muted);font-size:var(--text-md)}.from-domain{overflow-wrap:anywhere}.from-domain-select{min-width:0}.sender-error{color:var(--color-danger);font-size:var(--text-xs)}.toggle-host{position:relative}.field-toggles{position:absolute;top:0;right:0;display:flex;gap:var(--space-3)}.field-toggle{padding:0;border:0;background:none;color:var(--color-link);font:inherit;font-size:var(--text-sm);font-weight:var(--weight-medium);cursor:pointer;&:hover{text-decoration:underline}}.field{display:grid;gap:6px}.field-label{color:var(--color-text);font-size:var(--text-sm);font-weight:var(--weight-semibold)}.file-list{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:var(--space-2);margin:0;padding:0;list-style:none}.file{position:relative;display:flex;align-items:center;gap:var(--space-2);min-width:0;padding:var(--space-2) var(--space-1) var(--space-2) var(--space-3);overflow:hidden;border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg-raised);&.error{border-color:var(--color-danger);background:var(--color-danger-soft)}}.file-icon{display:flex;flex:none;color:var(--color-accent)}.file-text{display:grid;flex:1;min-width:0}.file-name{overflow:hidden;color:var(--color-text);font-size:var(--text-sm);font-weight:var(--weight-medium);text-overflow:ellipsis;white-space:nowrap}.file-meta{overflow:hidden;color:var(--color-text-muted);font-size:var(--text-xs);text-overflow:ellipsis;white-space:nowrap;font-variant-numeric:tabular-nums}.file.error .file-icon,.file.error .file-meta{color:var(--color-danger)}.file-progress{position:absolute;bottom:0;left:0;height:2px;background:var(--color-accent);transition:width var(--duration-normal) var(--ease)}.dropzone{display:flex;align-items:center;justify-content:center;gap:var(--space-3);width:100%;padding:var(--space-4);border:1px dashed var(--color-border-strong);border-radius:var(--radius-md);background:var(--color-bg-subtle);color:var(--color-text-muted);font:inherit;text-align:left;cursor:pointer;transition:border-color var(--duration-fast) var(--ease),background var(--duration-fast) var(--ease),color var(--duration-fast) var(--ease);&:hover,&:focus-visible{border-color:var(--color-accent);color:var(--color-text)}&:focus-visible{outline:0;box-shadow:0 0 0 3px var(--color-accent-soft)}}form.dragging .dropzone{border-style:solid;border-color:var(--color-accent);background:var(--color-accent-soft);color:var(--color-accent)}.dropzone-text{display:grid;gap:2px;font-size:var(--text-sm)}.dropzone-title{font-weight:var(--weight-medium)}.dropzone-link{color:var(--color-link)}.dropzone-hint{color:var(--color-text-subtle);font-size:var(--text-xs)}.file-input{display:none}.actions-grow{flex:1}.save-status{align-self:center;color:var(--color-text-muted);font-size:var(--text-sm);white-space:nowrap;&.failed{color:var(--color-danger);cursor:help}}form > .dialog-actions{flex-wrap:wrap} :mobile {form > .dialog-actions{position:sticky;bottom:0;z-index:1;padding:var(--space-3) 0;border-top:1px solid var(--color-border);background:var(--color-bg-raised)}}.templates{display:grid;gap:2px;padding:var(--space-2)}.templates-empty{padding:var(--space-2);color:var(--color-text-muted);font-size:var(--text-sm)}.template-row{display:flex;align-items:center;gap:var(--space-2);padding:var(--space-1) var(--space-1) var(--space-1) var(--space-2);border-radius:var(--radius-md);cursor:pointer;&:hover{background:var(--color-bg-hover)}}.template-text{display:grid;flex:1;min-width:0}.template-name{font-size:var(--text-sm);font-weight:var(--weight-semibold)}.template-preview{overflow:hidden;color:var(--color-text-muted);font-size:var(--text-xs);text-overflow:ellipsis;white-space:nowrap}.templates-hint{margin:0;padding:var(--space-1) var(--space-2);color:var(--color-text-muted);font-size:var(--text-xs)}.templates-foot{display:flex;justify-content:flex-end;padding-top:var(--space-1);border-top:1px solid var(--color-border)}.error-text{color:var(--color-danger);font-size:var(--text-xs);font-weight:var(--weight-medium)}.domain-note{color:var(--color-text-muted);font-size:var(--text-sm)}
}`

  HTML = `<form class:dragging={state.dragging} onsubmit="fez.send(event)" oninput="fez.changed()" onchange="fez.changed()" onclick="fez.changed()" ondragenter="fez.dragEnter(event)" ondragover="fez.dragOver(event)" ondragleave="fez.dragLeave(event)" ondrop="fez.drop(event)" onpaste="fez.paste(event)" novalidate>
<div class="sender" role="group" aria-label="From" onchange="fez.fromChanged(event)">
<span class="sender-label">From</span>
<div class="from-row">
<span class="from-name"><ui-select name="from_name" :options="state.fromNames" value={state.fromName}></ui-select></span>
<span class="from-host">
<span class="from-at">@</span>
{#if state.manyDomains}
<span class="from-domain-select"><ui-select name="from_domain" :options="state.fromDomains" value={state.fromDomain}></ui-select></span>
{:else}
<span class="from-domain">{state.fromDomain || 'no active domain'}</span>
{/if}
</span>
</div>
{#if state.errors.from}
<span class="sender-error" role="alert">{state.errors.from}</span>
{/if}
<input type="hidden" name="from" value={state.fromName ? \`\${state.fromName}|\${state.fromDomain}\` : ''}>
</div>
<div class="toggle-host">
<ui-input name="to" label="To" placeholder="ana@example.com" value={state.to} error={state.errors.to}></ui-input>
<span class="field-toggles">
{#unless state.showCc}
<button type="button" class="field-toggle" onclick="fez.openCc()">Cc</button>
{/unless}
{#unless state.showBcc}
<button type="button" class="field-toggle" onclick="fez.openBcc()">Bcc</button>
{/unless}
</span>
</div>
{#if state.showCc}
<ui-input name="cc" label="Cc" placeholder="Separate addresses with commas" value={state.cc} :autofocus="state.focusCc" error={state.errors.cc}></ui-input>
{/if}
{#if state.showBcc}
<ui-input name="bcc" label="Bcc" placeholder="Hidden from the other recipients" value={state.bcc} :autofocus="state.focusBcc" error={state.errors.bcc}></ui-input>
{/if}
<recipient-suggest></recipient-suggest>
<ui-input name="subject" label="Subject" value={state.subject} maxlength="500" error={state.errors.subject}></ui-input>
<ui-md-editor name="body" label="Message" rows="10" value={state.body}></ui-md-editor>
<div class="field">
<span class="field-label">Attachments</span>
{#if state.cards.length}
<ul class="file-list">
{#each state.cards as card}
<li key={card.id} class="file {card.status}" fez:in="fade, duration=150">
<span class="file-icon"><ui-icon name={card.icon} size="22"></ui-icon></span>
<span class="file-text">
<span class="file-name" title={card.filename}>{card.filename}</span>
<span class="file-meta">{card.meta}</span>
</span>
<ui-button size="sm" variant="ghost" icon="x" title="Remove" onclick="fez.removeAttachment('{card.id}')"></ui-button>
{#if card.status === 'uploading'}
<span class="file-progress" style="width: {card.percent}%"></span>
{/if}
</li>
{/each}
</ul>
{/if}
<button type="button" class="dropzone" onclick="fez.pickFiles()">
<ui-icon name="upload-simple" size="22"></ui-icon>
<span class="dropzone-text">
{#if state.dragging}
<span class="dropzone-title">Drop to attach</span>
{:else}
<span class="dropzone-title">Drop files here or <span class="dropzone-link">browse</span></span>
{/if}
<span class="dropzone-hint">Up to 8 MB each. Pasting a file into the message attaches it too.</span>
</span>
</button>
<input class="file-input" type="file" multiple fez:this="fileInput" onchange="fez.chosen(event)">
{#if state.errors.attachments}
<span class="error-text">{state.errors.attachments}</span>
{/if}
</div>
{#if state.showSchedule}
<div class="toggle-host">
<ui-input name="send_at" type="datetime-local" step="300" label="Send at" hint="Until then it waits in Sent and can be taken back." error={state.errors.send_at} :autofocus="true"></ui-input>
<span class="field-toggles"><button type="button" class="field-toggle" onclick="fez.closeSchedule()">Send now instead</button></span>
</div>
{/if}
{#if state.cannotSend}
<p class="domain-note">You read this mailbox but may not send from it; ask an admin of the container.</p>
{:else if !props.domains.length}
<p class="domain-note">Add and verify a domain to this container before sending.</p>
{/if}
{#if state.templatesOpen}
<div class="ui-card templates" fez:in="slide, duration=140">
{#each state.templates as template}
<div class="template-row" key={template.ref} onclick="fez.useTemplate('{template.ref}')" title="Insert {template.name}">
<ui-icon name="note" size="16"></ui-icon>
<span class="template-text">
<span class="template-name">{template.name}</span>
<span class="template-preview">{template.body}</span>
</span>
<ui-button size="sm" variant="ghost" icon="trash" title="Delete template" onclick="fez.deleteTemplate('{template.ref}', event)"></ui-button>
</div>
{:else}
<p class="templates-empty">No templates for this mailbox yet. Write a reply you send often, then save it here.</p>
{/each}
<p class="templates-hint">{state.templateHint}</p>
<div class="templates-foot"><ui-button size="sm" variant="ghost" icon="floppy-disk" label="Save this message as a template" onclick="fez.saveTemplate()"></ui-button></div>
</div>
{/if}
<div class="dialog-actions">
<ui-button icon="note" title="Templates" aria-label="Templates" onclick="fez.toggleTemplates()"></ui-button>
{#if state.saveLabel}
<span class="save-status" class:failed={state.saveStatus === 'error'} title={state.saveError || null} role="status">{state.saveLabel}</span>
{/if}
<span class="actions-grow"></span>
<ui-button icon="trash" title="Discard" aria-label="Discard" onclick="fez.discard()"></ui-button>
<ui-button icon="floppy-disk" label="Save draft" :loading="state.saving" :disabled="state.uploading" onclick="fez.saveDraft()"></ui-button>
{#unless state.showSchedule}
<ui-button icon="clock" label="Schedule" onclick="fez.openSchedule()"></ui-button>
{/unless}
<ui-button type="submit" variant="primary" icon="paper-plane-tilt" label={state.showSchedule ? 'Schedule send' : 'Send'} :loading="state.sending" :disabled="state.sendBlocked"></ui-button>
</div>
</form>`
};
}

Fez('mail-compose', __fez_component_0);

export default __fez_component_0;
